import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, X, Database, ChevronRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import VersionIndicator from "../components/common/VersionIndicator";
import AppLayout from "../layout/AppLayout";
import { orderService } from "../services/OrderService";
import LanguageSelector from "../components/common/LanguageSelector";
import CataxIcon from "../assets/CataxIcon";
import MobileHeader from "../layout/MobileHeader";
import PromptInput from "../components/features/chat/PromptInput";
import ChatService from "../services/ChatService";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrder } from "../contexts/OrderContext";
import { useSession } from "../contexts/SessionContext";
import { settingsService } from "../services/SettingsService";

const HomePage: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const { removeActiveOrderId, setOrderObject } = useOrder();
    const { setActiveSessionId } = useSession();
    const [sessionError, setSessionError] = useState<string | null>(null);
    const [availableCatalogs, setAvailableCatalogs] = useState<string[]>([]);
    const [catalogsLoading, setCatalogsLoading] = useState<boolean>(true);

    // Redirect inactive users to waiting approval page
    useEffect(() => {
        if (currentUser && currentUser.is_active === false) {
            navigate('/waiting-approval');
        }
    }, [currentUser, navigate]);

    // Load available catalogs
    useEffect(() => {
        const loadCatalogs = async () => {
            if (currentUser) {
                setCatalogsLoading(true);
                try {
                    const response = await settingsService.getUserSettings();
                    const catalogs = response.data?.settings?.catalogs_config || [];
                    // Dedupe case-insensitively (the same brand can be connected twice, e.g. "Kia" + "KIA"); keep first spelling.
                    const seen = new Set<string>();
                    const names = catalogs
                        .map((c: any) => (c.catalog_name || 'Unknown') as string)
                        .filter((name: string) => { const k = name.trim().toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
                    setAvailableCatalogs(names);
                } catch (error) {
                    console.error('Failed to load catalogs:', error);
                } finally {
                    setCatalogsLoading(false);
                }
            } else {
                setCatalogsLoading(false);
            }
        };
        loadCatalogs();
    }, [currentUser]);

    // The hero sentence is written by the model; until it lands (or if it never
    // does) the static greeting below stands in, so the headline can't flash
    // empty or shift the layout.
    const [headline, setHeadline] = useState<string | null>(null);
    useEffect(() => {
        if (!currentUser) return;
        orderService.getHomeHeadline(currentLanguage).then(setHeadline).catch(() => setHeadline(null));
    }, [currentUser, currentLanguage]);
    const profileName = [currentUser?.displayName, currentUser?.name]
        .find((value) => value?.trim() && !value.includes('@'));
    const firstName = profileName?.trim().split(/\s+/)[0] || "";

    const handlePromptSubmit = async (prompt: string, files: any[]) => {
        // Clear any previous errors
        setSessionError(null);
        setActiveSessionId(''); // Clear active session ID when starting a new one
        try {
            // call to backend to create a new session with the prompt and files
            const response = await ChatService.createSession(currentLanguage);
            if (response.success) {
                removeActiveOrderId(); // Clear any active order context when starting a new session
                setOrderObject(null); // Clear stored order object
                setActiveSessionId(response.sessionId || '');

                // Files were pre-uploaded in PromptInput on selection; pass them straight through
                const transformedFiles = files.map((file) => ({
                    data: file.base64?.split(',')[1] || '',
                    mimeType: file.type,
                    preview: file.preview || file.base64 || '',
                    name: file.name,
                    gcsUri: file.gcsUri,
                }));

                navigate(`/chat/${response.sessionId}`, {
                    state: {
                        prompt,
                        files: transformedFiles
                    }
                });
            } else {
                const errorMessage = response.error || t("homepage.errors.sessionCreationFailed", {
                    defaultValue: "Failed to create session. Please try again."
                });
                setSessionError(errorMessage);
                console.error("Failed to create session:", response.error);
            }
        } catch (error) {
            const errorMessage = t("homepage.errors.sessionCreationError", {
                defaultValue: "An error occurred while creating the session. Please try again."
            });
            setSessionError(errorMessage);
            console.error("Error creating session:", error);
        }
    }; return (
        <AppLayout>

            <MobileHeader title={t("header.home", { defaultValue: "" })} />
            <div className="flex-1 overflow-auto bg-main-bg min-h-full flex flex-col">

                <div className="flex-1 max-w-[1120px] mx-auto px-4 sm:px-8 py-6 sm:py-10 w-full flex flex-col">

                    {/* Language Selector - Top Right (only for non-logged-in users on desktop) */}
                    {!currentUser && (
                        <div className="hidden lg:flex justify-end mb-4">
                            <LanguageSelector requireAuth={false} />
                        </div>
                    )}

                    {/* Content */}
                    <div className="mt-auto flex flex-col items-stretch w-full gap-5 sm:gap-7">

                        {/* Hero Section */}
                        <div className={`w-full transition-all ${currentUser && !catalogsLoading && availableCatalogs.length === 0 ? 'opacity-40 blur-sm' : ''}`}>
                            <div className="flex flex-col items-center gap-3 sm:gap-6">
                                {currentUser ? (
                                    <div className="w-full flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 text-left">
                                        <div className="flex flex-col gap-1">
                                            <h1 className="text-2xl sm:text-[30px] sm:leading-9 font-semibold tracking-tight" style={{ color: '#201e1a', letterSpacing: '-0.02em' }}>
                                                {headline ? (
                                                    <span className="animate-fadeIn-text">{headline}</span>
                                                ) : (
                                                    <>
                                                        {t("homepage.greeting.hello", { defaultValue: "Hi" })}
                                                        {firstName && <> <span>{firstName}</span></>}
                                                        {t("homepage.greeting.subtitle", { defaultValue: ", what part are we looking for today?" })}
                                                    </>
                                                )}
                                            </h1>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <CataxIcon className="w-14 h-14 sm:w-28 sm:h-28 animate-bounce-slow" />
                                        <h1 className="text-2xl sm:text-4xl font-medium text-gray-900 animate-slide-in-right">
                                            {t("homepage.greeting.welcome", { defaultValue: "Welcome to Cata" })}
                                        </h1>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Main Input Area */}
                        <div className="w-full">

                            {/* Error Message */}
                            {sessionError && (
                                <div className="mb-3 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm text-red-800 font-medium">{sessionError}</p>
                                    </div>
                                    <button
                                        onClick={() => setSessionError(null)}
                                        className="text-red-400 hover:text-red-600 transition-colors"
                                        aria-label="Dismiss error"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}

                            {currentUser ? (
                                <>
                                    {catalogsLoading ? (
                                        <div className="w-full">
                                            <div className="relative">
                                                <PromptInput
                                                    onSubmit={() => { }}
                                                    placeholder={t("homepage.prompt.placeholder", { defaultValue: "Message Cata..." })}
                                                    availableCatalogs={[]}
                                                    disabled={true}
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 backdrop-blur-sm">
                                                    <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/80 px-6 py-5 sm:px-8 sm:py-6 max-w-xs sm:max-w-sm pointer-events-auto">
                                                        <div className="text-center space-y-3 sm:space-y-4">
                                                            <div className="flex justify-center">
                                                                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center shadow-sm">
                                                                    <div className="animate-spin rounded-full h-6 w-6 sm:h-7 sm:w-7 border-b-2 border-gray-500"></div>
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1.5 sm:space-y-2">
                                                                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                                                    {t("homepage.loading.title", { defaultValue: "Loading..." })}
                                                                </h3>
                                                                <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                                                                    {t("homepage.loading.description", { defaultValue: "Please wait while we load your catalogs" })}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : availableCatalogs.length === 0 ? (
                                        <div className="w-full">
                                            <div className="relative">
                                                <PromptInput
                                                    onSubmit={() => { }}
                                                    placeholder={t("homepage.prompt.placeholder", { defaultValue: "Message Cata..." })}
                                                    availableCatalogs={[]}
                                                    disabled={true}
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 backdrop-blur-sm">
                                                    <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/80 px-6 py-5 sm:px-8 sm:py-6 max-w-xs sm:max-w-sm pointer-events-auto">
                                                        <div className="text-center space-y-3 sm:space-y-4">
                                                            <div className="flex justify-center">
                                                                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center shadow-sm">
                                                                    <Database className="w-6 h-6 sm:w-7 sm:h-7 text-gray-500" />
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1.5 sm:space-y-2">
                                                                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                                                    {t("homepage.noCatalogs.title", { defaultValue: "No catalogs connected" })}
                                                                </h3>
                                                                <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                                                                    {t("homepage.noCatalogs.description", { defaultValue: "Connect a catalog to start using Cata AI" })}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={() => navigate("/settings?section=integrations&integration=catalog")}
                                                                className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-all shadow-sm hover:shadow-md"
                                                            >
                                                                <Database className="w-4 h-4" />
                                                                {t("homepage.noCatalogs.connectButton", { defaultValue: "Connect Catalog" })}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <PromptInput
                                            onSubmit={handlePromptSubmit}
                                            placeholder={t("homepage.prompt.placeholder", { defaultValue: "Message Cata..." })}
                                            availableCatalogs={availableCatalogs}
                                        />
                                    )}

                                </>
                            ) : (
                                <div className="mt-6 sm:mt-8 text-center">
                                    <div className="inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-3.5 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200/50 shadow-sm">
                                        <Sparkles className="w-4 h-4 text-gray-400" />
                                        <p className="text-gray-600 text-sm font-light">
                                            {t("homepage.auth.signInRequired", { defaultValue: "Sign in required" })}
                                        </p>
                                        <button
                                            onClick={() => navigate("/login")}
                                            className="mobile-touch-target ml-1 -my-2 px-1 text-gray-900 font-medium hover:text-gray-700 transition-colors flex items-center gap-1"
                                        >
                                            {t("homepage.auth.signIn", { defaultValue: "Sign In" })}
                                            <ChevronRight className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Footer */}
                    <footer className="text-center py-4 sm:py-8 text-gray-500 mt-auto">
                        <p className="text-xs font-light">{t("homepage.footer.copyright")}</p>
                        <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                            <div className="flex items-center gap-3 sm:gap-4">
                                <Link to="/terms" className="mobile-touch-target inline-flex items-center text-xs text-gray-400 hover:text-gray-600 transition-colors font-light">
                                    {t("terms.title")}
                                </Link>
                                <span className="text-gray-300">•</span>
                                <Link to="/privacy" className="mobile-touch-target inline-flex items-center text-xs text-gray-400 hover:text-gray-600 transition-colors font-light">
                                    {t("privacy.title")}
                                </Link>
                                <span className="text-gray-300">•</span>
                                <Link to="/cookie-settings" className="mobile-touch-target inline-flex items-center text-xs text-gray-400 hover:text-gray-600 transition-colors font-light">
                                    {t("cookieSettings.title")}
                                </Link>
                            </div>
                            <VersionIndicator className="text-gray-400 text-xs" showIcon={true} />
                        </div>
                    </footer>
                </div>
            </div>
        </AppLayout>
    );
};
export default HomePage;
